import {useState, useEffect, useRef} from 'react'
import {
	getLevelProgressionFormattedData,
	parseDurationList,
	updateInfoData,
	initializeRegEndTime,
} from 'shared/lib/gameLogic'

const usePreviewGameLogic = (eventData) => {
	const [formattedGameData, setFormattedGameData] = useState(null)
	const [infoData, setInfoData] = useState([])
	// Mock timeData for calculateBigBlind
	const timeDataRef = useRef({
		level: '1',
		bb: '0',
	})

	useEffect(() => {
		if (!eventData || !eventData.duration_list) return

		let intervalId = null

		try {
			const durationList = eventData.duration_list
			const parsedDurationList = parseDurationList(durationList).map(
				(item, index) => {
					// If level number (item[0]) is null/empty, use index + 1
					if (!item[0]) {
						const newItem = [...item]
						newItem[0] = (index + 1).toString()
						return newItem
					}
					return item
				}
			)

			// Initialize Mock GameState
			const gameState = {
				durationIndex: -1,
				totalMinute: 0,
				startTime: Date.now(), // Simulation start time
				regEndMinute: 0,
				nextBreakMinute: 0,
				isEarlyEnd: false,
				isRegEnd: false,
			}

			// Calculate Reg End Time
			if (eventData.reg_end_level) {
				gameState.regEndMinute = initializeRegEndTime(
					parsedDurationList,
					eventData.reg_end_level
				)
			}

			const mappedEventData = {
				...eventData,
				// Map preview fields to expected game logic fields
				rest_player: eventData.ticket_number || eventData.max_entry || 0,
				total_player: eventData.ticket_number || eventData.max_entry || 0,
				re_entry: eventData.re_entry || 0,
				early_count: 0,
				add_on: eventData.add_on || 0,
				total_stack:
					parseInt(eventData.starting_chip || 0) *
					parseInt(eventData.ticket_number || eventData.max_entry || 0),
				avg_stack: parseInt(eventData.starting_chip || 0),
				// Ensure optional fields are present for logic checks
				early_end_level: eventData.early_end_level,
				reg_end_level: eventData.reg_end_level,
			}

			// Function to update game state based on elapsed time (simulated)
			const updateSimulation = (elapsedSeconds) => {
				// getLevelProgressionFormattedData expects 4th arg as 'elapsedSeconds'
				// IF we look at the source code of getLevelProgressionFormattedData,
				// it usually takes (durationList, gameState, eventData, elapsedSeconds).
				// Let's verify if the 4th arg is indeed elapsedSeconds (in seconds).
				// In the previous code it was passed as '0'.

				const formattedData = getLevelProgressionFormattedData(
					parsedDurationList,
					gameState,
					mappedEventData,
					elapsedSeconds
				)

				// Update timeData ref for info calculation
				timeDataRef.current = {
					level: formattedData.level,
					bb: formattedData.bb,
				}

				setFormattedGameData(formattedData)

				// Calculate Info Data (Players, Chips, etc.)
				updateInfoData(
					formattedData,
					setInfoData,
					parsedDurationList,
					gameState,
					timeDataRef.current
				)
			}

			// Initial calculation
			let currentElapsed = 0
			updateSimulation(currentElapsed)

			// Start simulation loop
			intervalId = setInterval(() => {
				currentElapsed += 1
				updateSimulation(currentElapsed)
			}, 1000)
		} catch (error) {
			console.error('Error calculating preview game logic:', error)
		}

		return () => {
			if (intervalId) clearInterval(intervalId)
		}
	}, [eventData])

	return {formattedGameData, infoData}
}

export default usePreviewGameLogic
